import { Children, type ReactElement, type ReactNode } from 'react';
import { s, type SpacingToken } from '@/theme/tokens';
import { Box, Stack, Surface, Text } from '@/ui';
import { Inline } from '@/ui/layout/Inline';
import type { SxProps, Theme } from '@/ui/primitives/types';

export type FormCardProps = {
  title?: string;
  titleSlot?: ReactNode;
  titleSx?: SxProps<Theme>;
  description?: ReactNode;
  topSlot?: ReactNode;
  headerSlot?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  padding?: SpacingToken | null;
  gap?: SpacingToken;
  contentGap?: SpacingToken;
  headerGap?: SpacingToken;
  footerGap?: SpacingToken;
  container?: 'surface' | 'none';
  border?: boolean;
  sx?: SxProps<Theme>;
};

export const FormCard = ({
  title,
  titleSlot,
  titleSx,
  description,
  topSlot,
  headerSlot,
  footer,
  children,
  padding = 'lg',
  gap = 'md',
  contentGap = 'md',
  headerGap = 'sm',
  footerGap = 'md',
  container = 'surface',
  border = false,
  sx,
}: FormCardProps): ReactElement => {
  const resolvedTitleSx = titleSx
    ? ([{ pl: s('none') }, ...(Array.isArray(titleSx) ? titleSx : [titleSx])] as SxProps<Theme>)
    : ({ pl: s('none') } as SxProps<Theme>);
  const hasHeader = Boolean(title ?? description ?? headerSlot);
  const hasContent = children !== null && children !== undefined && children !== false;
  const resolvedTopSlot = Children.toArray(topSlot);
  const resolvedHeaderSlot = Children.toArray(headerSlot);
  const resolvedChildren = Children.toArray(children);
  const resolvedFooter = Children.toArray(footer);

  const content = (
    <Stack gap={gap}>
      {resolvedTopSlot.length > 0 ? <Box>{resolvedTopSlot}</Box> : null}
      {hasHeader ? (
        <Stack gap={headerGap}>
          {title ? (
            titleSlot ? (
              <Inline justify="space-between" align="center">
                <Text role="sectionTitle" sx={resolvedTitleSx}>
                  {title}
                </Text>
                <Box>{titleSlot}</Box>
              </Inline>
            ) : (
              <Text role="sectionTitle" sx={resolvedTitleSx}>
                {title}
              </Text>
            )
          ) : null}
          {description ? (
            typeof description === 'string' ? (
              <Text
                role="body"
                color="textSecondary"
                sx={{
                  fontSize: (theme) => theme.typography.subtitle1.fontSize,
                  lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
                }}
              >
                {description}
              </Text>
            ) : (
              description
            )
          ) : null}
          {resolvedHeaderSlot.length > 0 ? <Box>{resolvedHeaderSlot}</Box> : null}
        </Stack>
      ) : null}
      {hasContent ? <Stack gap={contentGap}>{resolvedChildren}</Stack> : null}
      {resolvedFooter.length > 0 ? (
        <Box
          sx={{
            mt: s(footerGap),
            fontSize: (theme) => theme.typography.subtitle1.fontSize,
            lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
          }}
        >
          {resolvedFooter}
        </Box>
      ) : null}
    </Stack>
  );

  if (container === 'none') {
    const baseSx = padding ? ({ p: s(padding) } satisfies SxProps<Theme>) : null;
    const resolvedSx = sx
      ? ([baseSx, ...(Array.isArray(sx) ? sx : [sx])].filter(Boolean) as SxProps<Theme>)
      : (baseSx ?? undefined);
    return <Box sx={resolvedSx}>{content}</Box>;
  }

  return (
    <Surface padding={padding} border={border} sx={sx}>
      {content}
    </Surface>
  );
};
